import { useState } from 'react'
import { Plus, Pencil } from 'lucide-react'
import { IconButton } from '@doculegis/shared'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  CustomRoleSheet,
  type CustomRoleSheetDraft,
} from '@/components/custom-roles/CustomRoleSheet'
import { usePermission } from '@/contexts/AuthContext'
import { useCustomRolesDB } from '@/hooks/useSupabase'

interface UserRoleSelectProps {
  value: string
  onChange: (value: string) => void
}

export function UserRoleSelect({ value, onChange }: UserRoleSelectProps) {
  const { roles } = useCustomRolesDB()
  const canCreate = usePermission('customRole', 'create')
  const canUpdate = usePermission('customRole', 'update')
  const hasSelection = value.trim() !== ''
  const [sheetOpen, setSheetOpen] = useState(false)
  const [sheetDraft, setSheetDraft] = useState<CustomRoleSheetDraft | null>(null)

  function openCreate() {
    setSheetDraft({
      mode: 'create',
      name: '',
      role_key: '',
      instructions: '',
    })
    setSheetOpen(true)
  }

  function openEditSelected() {
    const role = roles.find(item => item.role_key === value)
    if (!role) return

    if (role.is_system) {
      setSheetDraft({
        mode: 'customize',
        name: role.name,
        role_key: role.role_key,
        instructions: role.instructions || '',
      })
    } else {
      setSheetDraft({
        mode: 'edit',
        id: role.id,
        name: role.name,
        role_key: role.role_key,
        instructions: role.instructions || '',
      })
    }
    setSheetOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">Your Role</label>
        <div className="flex gap-2">
          {canCreate ? (
            <IconButton
              type="button"
              size="sm"
              onClick={openCreate}
              title="Add role"
              aria-label="Add role"
            >
              <Plus className="h-4 w-4" />
            </IconButton>
          ) : null}
          {canUpdate && hasSelection ? (
            <IconButton
              type="button"
              size="sm"
              onClick={openEditSelected}
              title="Edit instructions"
              aria-label="Edit instructions"
            >
              <Pencil className="h-4 w-4" />
            </IconButton>
          ) : null}
        </div>
      </div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select your role" />
        </SelectTrigger>
        <SelectContent>
          {roles.filter(role => role.is_system).map(role => (
            <SelectItem key={role.id} value={role.role_key}>{role.name}</SelectItem>
          ))}
          {roles.some(role => !role.is_system) && (
            <SelectItem value="__separator__" disabled>
              ──────────
            </SelectItem>
          )}
          {roles.filter(role => !role.is_system).map(role => (
            <SelectItem key={role.id} value={role.role_key}>{role.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <CustomRoleSheet
        open={sheetOpen}
        onOpenChange={v => {
          setSheetOpen(v)
          if (!v) setSheetDraft(null)
        }}
        draft={sheetDraft}
        onSaved={onChange}
      />
    </div>
  )
}
